import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight, DollarSign, TrendingUp } from 'lucide-react';

const formatMoney = (amount, currency) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount || 0);

const Card = ({ children, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
    className="bg-white border border-slate-200 p-6 rounded-sm hover:border-slate-400 transition-colors h-full"
    data-testid={testId}
  >
    {children}
  </motion.div>
);

export const KpiIncomeWidget = ({ data }) => (
  <Card testId="widget-kpi-income">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">Total Income</span>
      <ArrowUpRight className="text-emerald-600" size={20} />
    </div>
    <div className="text-3xl font-chivo font-bold text-slate-900 tabular-nums" data-testid="widget-kpi-income-value">
      ${data?.summary?.total_income?.toLocaleString() || 0}
    </div>
  </Card>
);

export const KpiExpensesWidget = ({ data }) => (
  <Card testId="widget-kpi-expenses">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">Total Expenses</span>
      <ArrowDownRight className="text-red-600" size={20} />
    </div>
    <div className="text-3xl font-chivo font-bold text-slate-900 tabular-nums" data-testid="widget-kpi-expenses-value">
      ${data?.summary?.total_expenses?.toLocaleString() || 0}
    </div>
  </Card>
);

export const KpiProfitWidget = ({ data }) => {
  const profit = data?.summary?.profit ?? 0;
  return (
    <Card testId="widget-kpi-profit">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">Net Profit</span>
        <TrendingUp className={profit >= 0 ? 'text-emerald-600' : 'text-red-600'} size={20} />
      </div>
      <div
        className={`text-3xl font-chivo font-bold tabular-nums ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
        data-testid="widget-kpi-profit-value"
      >
        ${profit.toLocaleString()}
      </div>
      {data?.summary?.profit_base != null && data?.summary?.by_currency?.length > 1 && (
        <div className="mt-1 text-xs font-ibm text-slate-500 tabular-nums" data-testid="widget-kpi-profit-base">
          ≈ {formatMoney(data.summary.profit_base, data.summary.base_currency)} {data.summary.base_currency} (FX)
        </div>
      )}
    </Card>
  );
};

export const KpiTransactionsWidget = ({ data }) => (
  <Card testId="widget-kpi-transactions">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">Transactions</span>
      <DollarSign className="text-slate-900" size={20} />
    </div>
    <div className="text-3xl font-chivo font-bold text-slate-900 tabular-nums" data-testid="widget-kpi-transactions-value">
      {data?.summary?.transaction_count || 0}
    </div>
  </Card>
);
